import axios from "axios";
import { LOGIN_URL } from "@/config";
import { showToast } from "vant";
import { ResultEnum } from "@/enum/httpEnum";
import { checkStatus } from "./helper/checkStatus";
import { AxiosCanceler } from "./helper/axiosCancel";
import { useAuthStore } from "@/stores/modules/auth";
import { useGlobalStore } from "@/stores/modules/global";
import i18n from "@/language";
import router from "@/routers";
import { getLocalData } from "@/utils/storage";

import type { AxiosInstance, AxiosError, AxiosRequestConfig, InternalAxiosRequestConfig, AxiosResponse } from "axios";

export interface CustomAxiosRequestConfig extends InternalAxiosRequestConfig {
	noLoading?: boolean;
}

type RequestHttpConfig = {
	[P in keyof CustomAxiosRequestConfig]?: CustomAxiosRequestConfig[P];
};

const i18nGlobal = i18n.global as any;

const axiosCancel = new AxiosCanceler();

const config = {
	// 默认地址请求地址，可在 .env.** 文件中修改
	baseURL: import.meta.env.VITE_API_URL,
	// 设置超时时间
	timeout: ResultEnum.TIMEOUT as number,
	// 跨域时候允许携带凭证
	withCredentials: false
};

class RequestHttp {
	service: AxiosInstance;
	public constructor(config: AxiosRequestConfig) {
		// instantiation
		this.service = axios.create(config);

		/**
		 * @description 请求拦截器
		 * 客户端发送请求 -> [请求拦截器] -> 服务器
		 * token校验(JWT) : 接受服务器返回的 token,存储到 vuex/pinia/本地储存当中
		 */
		this.service.interceptors.request.use(
			(config: CustomAxiosRequestConfig) => {
				const authStore = useAuthStore();
				const globalStore = useGlobalStore();
				axiosCancel.removePending(config);
				axiosCancel.addPending(config);
				if (config.headers && typeof config.headers.set === "function") {
					config.headers.set("Authorization", authStore.tokenGet);
					config.headers.set("X-Channel-Code", "frontend");
					config.headers.set("X-App-Code", "marketing");
					config.headers.set("X-Locale", globalStore.languageGet);
					config.headers.set("X-Device-Id", getLocalData("fingerprint"));
					config.headers.set("X-Ticket", authStore.xTicketGet);
				}
				return config;
			},
			(error: AxiosError) => {
				return Promise.reject(error);
			}
		);

		/**
		 * @description 响应拦截器
		 *  服务器换返回信息 -> [拦截统一处理] -> 客户端JS获取到信息
		 */
		this.service.interceptors.response.use(
			async (response: AxiosResponse) => {
				axiosCancel.removePending(response.config);
				const { data } = response;
				const authStore = useAuthStore();
				// 登陆失效
				if (data.status == ResultEnum.OVERDUE) {
					if (authStore.tokenGet) {
						authStore.logout();
					}
					router.replace(LOGIN_URL);
					showToast(data.msg);
					return Promise.reject(data);
				}
				// 全局错误信息拦截（防止下载文件的时候返回数据流，没有 code 直接报错）
				if (data.status && data.status !== ResultEnum.SUCCESS) {
					showToast(data.msg);
					return Promise.reject(data);
				}
				// 成功请求（在页面上除非特殊情况，否则不用处理失败逻辑）
				return data;
			},
			async (error: AxiosError<API.Response<null>>) => {
				const { response } = error;
				// 请求超时 && 网络错误单独判断，没有 response
				if (error.message.indexOf("timeout") !== -1) {
					showToast(i18nGlobal.t("msg.Network.timedout"));
					return Promise.reject(error);
				}
				if (error.message.indexOf("Network Error") !== -1) {
					showToast(i18nGlobal.t("msg.Network.error"));
					return Promise.reject(error);
				}
				// 根据服务器响应的错误状态码，做不同的处理
				if (response) {
					const authStore = useAuthStore();
					checkStatus(response?.data?.status ?? 0, response.data.msg, response.data.msgVars);
					// 登陆失效
					if (response.status == ResultEnum.OVERDUE) {
						if (authStore.tokenGet) {
							authStore.logout();
						}
						router.replace(LOGIN_URL);
					}

					return Promise.reject(response.data);
				}
				// 服务器结果都没有返回(可能服务器错误可能客户端断网)，断网处理:可以跳转到断网页面
				if (!window.navigator.onLine) {
					router.replace("/500");
					return Promise.reject(error);
				}
				return Promise.reject(error);
			}
		);
	}

	/**
	 * @description 常用请求方法封装
	 * return http.post<Auth.ResLogin>(PORT1 + `/login`, params, { noLoading: true }); // 正常 post json 请求  ==>  application/json
	 * return http.post<Auth.ResLogin>(PORT1 + `/login`, params, { noLoading: true }); // 控制当前请求不显示 loading
	 * return http.post<Auth.ResLogin>(PORT1 + `/login`, {}, { params }); // post 请求携带 query 参数  ==>  ?username=admin&password=123456
	 * return http.get<Auth.ResLogin>(PORT1 + `/login?${qs.stringify(params, { arrayFormat: "repeat" })}`); // get 请求可以携带数组等复杂参数
	 */
	get<T>(url: string, params?: object, _object?: RequestHttpConfig): Promise<T> {
		return this.service.get(url, { params, ..._object });
	}
	post<T>(url: string, params?: object | string, _object?: RequestHttpConfig): Promise<T> {
		return this.service.post(url, params, _object);
	}
	put<T>(url: string, params?: object, _object?: RequestHttpConfig): Promise<T> {
		return this.service.put(url, params, _object);
	}
	delete<T>(url: string, params?: never, _object?: RequestHttpConfig): Promise<T> {
		return this.service.delete(url, { data: params, ..._object });
	}
	download(url: string, params?: object, _object?: RequestHttpConfig): Promise<BlobPart> {
		return this.service.post(url, params, { ..._object, responseType: "blob" });
	}
}

export default new RequestHttp(config);
